<script setup lang="ts">
import type { IconProps } from '@/components/base/icon/Icon.vue';
import { Loader } from 'lucide';
import { computed, onMounted, ref } from 'vue';
import { SphIcon } from '@/components/base/icon';
import { cn } from '@/utils/shadcn/lib/utils.ts';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<SpinnerProps>(), {
    class: '',
    size: undefined,
    variant: 'inline',
    show: true,
});

export interface SpinnerProps extends Pick<IconProps, 'size'> {
    class?: string
    /**
     * How the spinner is placed.
     * - `inline`: renders inline, in the document flow.
     * - `centered`: fills its container and centers the spinner in it.
     * - `overlay`: covers its container and centers the spinner on top of the
     *   content behind it. The container needs its own positioning context.
     */
    variant?: 'inline' | 'centered' | 'overlay'
    show?: boolean
}

const spinnerSize = computed(() => props.size || (props.variant === 'inline' ? 'md' : 'xl'));

const mounted = ref(false);
onMounted(() => {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            mounted.value = true;
        });
    });
});

const spinnerClass = computed(() => cn(
    '[&>svg]:animate-[spin_1.25s_linear_infinite] opacity-0 transition-opacity duration-300',
    mounted.value && 'opacity-100',
));

const wrapperClass = computed(() => cn(
    'flex items-center justify-center',
    props.variant === 'overlay' ? 'absolute inset-0 z-10 bg-background/70' : 'h-full w-full',
    props.class,
));
</script>

<template>
    <transition
        name="quick-fade"
    >
        <template v-if="props.show">
            <SphIcon
                v-if="props.variant === 'inline'"
                :icon="Loader"
                :size="spinnerSize"
                :class="cn(spinnerClass, props.class)"
            />
            <div
                v-else
                :class="wrapperClass"
            >
                <SphIcon
                    :icon="Loader"
                    :size="spinnerSize"
                    :class="spinnerClass"
                />
            </div>
        </template>
    </transition>
</template>
